// PlanCards — les trois plans, et le bouton qui ouvre la caisse.
//
// ── POURQUOI UN COMPOSANT, ET PAS DEUX GRILLES ──────────────────────────────
//
// Ces cartes se montrent à DEUX endroits : la page Compte, et le premier écran de quelqu'un qui
// n'a pas encore de plan. Recopiées, elles se contrediraient au premier changement de prix — et
// un prix faux sur un écran d'achat n'est pas un défaut d'affichage, c'est une promesse qu'on ne
// tient pas.
//
// IL NE SAIT RIEN. Les libellés viennent de `planButton` (lib/account_view, pur et éprouvé) et le
// clic remonte à l'appelant : c'est lui qui ouvre la caisse, parce que c'est lui qui sait quoi
// faire après — rester sur l'écran, ou en changer.
import type { AccountView, Plan } from "../api";
import { formatUsdCents, formatBytes, planButton, entryPlan, priceLine, PLAN_NAME } from "../lib/account_view";

const GB = 1024 ** 3;

export type PlanCardsProps = {
  readonly plans: readonly Plan[];
  /** Ce que le compte tient déjà. Le premier jour : `plan: null`. */
  readonly view: Pick<AccountView, "plan" | "plan_ends_at">;
  /** Le plan dont la caisse s'ouvre, ou `null`. Un seul à la fois. */
  readonly busy: string | null;
  readonly onPick: (id: Plan["id"]) => void;
  /** Plus d'air entre et dans les cartes : la page d'accueil, où elles sont seules à l'écran. */
  readonly roomy?: boolean;
};

export default function PlanCards({ plans, view, busy, onPick, roomy = false }: PlanCardsProps) {
  const entree = entryPlan(plans, view);
  return (
    // As many columns as cards: one card alone is not a grid with two holes beside it.
    <ul className={`grid grid-cols-1 text-left ${roomy ? "gap-6 md:gap-8" : "gap-4"} ${plans.length >= 3 ? "md:grid-cols-3" : plans.length === 2 ? "md:grid-cols-2" : "mx-auto max-w-sm"}`}>
      {plans.map((p) => {
        const b = planButton(view, p.id, busy);
        const sien = view.plan?.id === p.id;
        const plein = entree?.id === p.id;
        return (
          <li
            key={p.id}
            // LE CADRE ACCENTUÉ DIT « LE TIEN », jamais « le recommandé ». Une carte mise en avant
            // pour vendre est le genre de détail qui fait douter du reste de l'écran.
            className={`flex min-w-0 flex-col rounded-2xl border bg-surface transition ${roomy ? "gap-4 p-7" : "gap-3 p-5"} ${sien ? "border-accent" : "border-border hover:border-border-active"}`}
          >
            <p className="text-sm font-semibold">{PLAN_NAME[p.id]}</p>
            {/* `$15.99`, PAS `15.99 $` : la console est en anglais, et le symbole y précède le
                nombre. L'écriture française traînait ici depuis l'écran Compte — sur un prix, ce
                n'est pas un détail de goût, c'est ce qui fait hésiter avant de payer.
                `tabular-nums` : trois prix côte à côte dont les chiffres ne s'alignent pas se
                lisent comme trois gabarits différents. */}
            {/* The price in the plan's own words (lib/account_view priceLine): the hosting plan in
                euros with VAT on top and its yearly price, a legacy plan in its dollars. */}
            <p className="text-2xl font-semibold tracking-tight tabular-nums">
              {priceLine(p).split(" / month")[0]}<span className="text-sm font-normal tracking-normal text-text-secondary"> / month{priceLine(p).split(" / month")[1] ?? ""}</span>
            </p>
            <ul className="flex flex-col gap-1 text-[13px] text-text-secondary">
              <li>{p.apps} {p.apps === 1 ? "app" : "apps"}</li>
              <li>{p.credit_usd_per_month > 0 ? `${formatUsdCents(Math.round(p.credit_usd_per_month * 100))} of build credit a month` : "Build credit as top-ups, never expires"}</li>
              <li>{formatBytes(p.gb * GB)} of storage</li>
            </ul>
            <button
              type="button"
              onClick={() => onPick(p.id)}
              disabled={b.disabled}
              // `border border-transparent` SUR LE BOUTON PLEIN : sans elle il mesure 2 px de
              // moins que ses voisins bordés, et trois boutons qui ne s'alignent pas à la base
              // sont exactement ce qu'on voit sans savoir le nommer.
              className={`mt-auto w-full rounded-md border px-3 py-2 text-[13px] font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
                plein
                  ? "border-transparent bg-accent text-white hover:bg-accent-hover disabled:hover:bg-accent"
                  : "border-border-active text-text hover:border-accent hover:text-accent disabled:hover:border-border-active disabled:hover:text-text"
              }`}
            >
              {b.label}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
